'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Wrapper,
  IframePaddingContainer,
  IframeRoundContainer,
  PageWrapper,
  GameUiContainer,
  GameBlock,
  ScreenBlock,
  GameUiSubContainer,
  DemoSwitcher,
  FullscreenButton,
  FavoriteButton,
  FullScreenImage,
} from './styles-desktop';
import useSWR from 'swr';
import { fetcher, fetcherWithToken } from '@/shared/api/fetcher';
import { HeadingRegularQuinary } from '@/shared/ui/typography';
import { Box } from '@/shared/ui/box';
import { useCurrentLocale, useScopedI18n } from '@locales/client';
import { BurnWarningWrapper } from '../bonuses/ui/styles';
import BurnWarningIcon from '@/shared/assets/burn-warning.svg';
import { useParams } from 'next/navigation';
import { processText } from '@/shared/libs/process-text';
import { useRouter } from 'next-nprogress-bar';
import { useFullscreen } from '@/flat-pages/game/FullscreenContext';
import { NavigationMenu } from '@/entities/navigation-menu/NavigationMenu';
import { useVisibleState } from '@/shared/hooks';
import { NotAlowedModal } from '@/entities/game/ui/not-alowed-modal';
import { Footer } from '@/shared/ui/footer';
import { WishlistToggle } from '@/features/wishlist/ui_ver2/wishlist-toggle';
import { BodyTextRegularC } from '@/shared/ui/typography2';
import { PwCheckbox } from '@/flat-pages/enter-game/PwCheckbox';
import JackpotGameWidget from '@/widgets/jackpot-component/JackpotGameWidget';

export const GameDesktop = ({
  fullScreenModeOn = false,
  isDemo = false,
  startMenu = false,
}) => {
  const [customFullscreen, setCustomFullscreen] = useState(false);

  const locale = useCurrentLocale();
  const {
    open: openNotAlowedModal,
    visible: visibleNotAlowedModal,
    close: closeNotAlowedModal,
  } = useVisibleState(false);

  const [isExiting, setIsExiting] = useState(false);
  const commonLoc = useScopedI18n('common');
  const cardLoc = useScopedI18n('common.game.card');
  const params = useParams();

  const { data: dataGames } = useSWR('/games', fetcher);

  const findInfoGame = useMemo(() => {
    return dataGames?.find((el) => processText(el?.title) === params.titleGame);
  }, [dataGames, params]);

  const REVALIDATE = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };
  const { data, error, isLoading } = useSWR(
    findInfoGame?.id ? ['game', findInfoGame.id, isDemo] : null,
    () => {
      const params = new URLSearchParams({
        id: findInfoGame.id,
        demo: isDemo.toString(),
      });

      return fetcherWithToken(`/game?${params.toString()}`);
    },
    REVALIDATE
  );
  const router = useRouter();
  const fsRef = useRef(null);
  const isRedirectingRef = useRef(false);

  useEffect(() => {
    const checkLiveChat = () => {
      if (window.LiveChatWidget) {
        if (window.LiveChatWidget._h) {
          window.LiveChatWidget.call('hide');
        } else {
          window.LiveChatWidget.on('ready', () => {
            window.LiveChatWidget.call('hide');
          });
        }
      } else {
        setTimeout(checkLiveChat, 500);
      }
    };

    checkLiveChat();
    // document.body.classList.add('--fill');
    // const chatWidget = document.getElementById('chat-widget-minimized');
    // chatWidget?.style.setProperty('display', 'none');
    //
    // return () => {
    //   document.body.classList.remove('--fill');
    //   chatWidget?.style.setProperty('display', 'block');
    // };
  }, []);

  const { isFullscreen, setIsFullscreen, setHeaderEnabled } = useFullscreen();
  useEffect(() => {
    setHeaderEnabled(false);
    return () => {
      setHeaderEnabled(true);
    };
  }, []);

  function gameRequestFullscreen() {
    if (document.fullscreenElement) {
      return;
    }
    if (!fullscreenAvailable()) {
      setCustomFullscreen(true);
      setIsFullscreen(true);
      setTimeout(() => {
        window.scrollTo(0, 1);
      }, 100);
      return;
    }
    if (fsRef.current) {
      fsRef.current.requestFullscreen();
    }
  }

  function gameRequestExitFullscreen() {
    if (!fullscreenAvailable()) {
      setCustomFullscreen(false);
      setIsFullscreen(false);
      return;
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.log(err);
      });
    }
  }

  useEffect(() => {
    const handleMessage = (event) => {
      if (isRedirectingRef.current) return;

      // exit
      if (event.data?.event === 'game_pause' && event.data?.data === null) {
        isRedirectingRef.current = true;
        setIsExiting(true);
        window.removeEventListener('message', handleMessage);
        router.push('/');
        return;
      }

      if (event.data === 'request-fullscreen') {
        if (isFullscreen) {
          gameRequestExitFullscreen();
        } else {
          gameRequestFullscreen();
        }
      }
      if (event.data === 'request-exit-fullscreen') {
        gameRequestExitFullscreen();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      isRedirectingRef.current = false;
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (fullScreenModeOn) {
      gameRequestFullscreen();
    }
  }, [fullScreenModeOn, gameRequestFullscreen]);

  if (isLoading || !data?.url) {
    return null;
  }

  const jackpotOn =
    findInfoGame?.providerId === 'vamos' && findInfoGame?.type === 'slots';

  if (error) {
    console.error('Game loading error:', error);
    return (
      <Wrapper isFullscreen={isFullscreen}>
        <Box $paddingTop={200} $paddingBottom={100}>
          {error.message === 'game_with_denied_bonus' ? (
            <>
              <Box $marginTop={24} $marginBottom={24}>
                <BurnWarningWrapper>
                  <BurnWarningIcon />
                </BurnWarningWrapper>
              </Box>
              <HeadingRegularQuinary color="#AFAFAF">
                {commonLoc('game.game_with_denied_bonus')}
              </HeadingRegularQuinary>
            </>
          ) : (
            <HeadingRegularQuinary color="#AFAFAF">
              {commonLoc('game.loading_error')}
            </HeadingRegularQuinary>
          )}
        </Box>
      </Wrapper>
    );
  }

  function fullscreenAvailable() {
    return !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullscreenEnabled ||
      document.msFullscreenEnabled
    );
  }

  function fullscreenHandler() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.log(err);
      });
    } else if (fsRef.current) {
      fsRef.current.requestFullscreen();
    }
  }

  function exitHandler() {
    if (fsRef.current) {
      router.push('/');
    }
  }

  function onFullscreenClickHandler() {
    if (isFullscreen) {
      gameRequestExitFullscreen();
    } else {
      gameRequestFullscreen();
    }
  }

  function getGameUrl() {
    return jackpotOn
      ? // ? data.url + '&disableHOME=true' + '&forcedFSButton=true'
        data.url + '&disableHOME=true' + '&disableFS=true'
      : data.url;
  }

  const getGamePath = (game, demo = false) => {
    const { providerId, title } = game;
    const basePath = `/${locale}/games/${processText(providerId)}/${processText(title)}`;
    return `${basePath}/${demo ? 'demo' : 'play'}`;
  };

  function onDemoSwitcherClick() {
    router.push(getGamePath(findInfoGame, !isDemo));
  }

  return (
    <PageWrapper>
      <ScreenBlock>
        <GameBlock>
          <Wrapper $isFullscreen={isFullscreen} $isExit={isExiting}>
            {jackpotOn && !customFullscreen && !isDemo && <JackpotGameWidget />}
            <IframePaddingContainer>
              <IframeRoundContainer>
                <iframe
                  key="game"
                  ref={fsRef}
                  className="game-iframe"
                  // allow="fullscreen"
                  src={getGameUrl()}
                  title={findInfoGame?.title || 'Game'}
                  loading="eager"
                />
              </IframeRoundContainer>
            </IframePaddingContainer>
          </Wrapper>
        </GameBlock>
        <GameUiContainer>
          <GameUiSubContainer>
            <FullscreenButton onClick={onFullscreenClickHandler}>
              <FullScreenImage />
            </FullscreenButton>
            <WishlistToggle
              gameId={findInfoGame?.id}
              Container={FavoriteButton}
              iconSize={24}
              checkedPath="/images/icons/heart-filled-ver2.svg"
              uncheckedPath="/images/icons/heart-unfilled-ver3.svg"
            />
          </GameUiSubContainer>
          <DemoSwitcher>
            <BodyTextRegularC>{cardLoc('demo')}</BodyTextRegularC>
            <PwCheckbox
              id="my-checkbox"
              checked={!isDemo}
              onClickHandler={onDemoSwitcherClick}
            />
            <BodyTextRegularC>{cardLoc('play')}</BodyTextRegularC>
          </DemoSwitcher>
        </GameUiContainer>
      </ScreenBlock>
      <NavigationMenu />
      <Footer />
      <NotAlowedModal
        visible={visibleNotAlowedModal}
        onClose={closeNotAlowedModal}
        onOpen={openNotAlowedModal}
      />
    </PageWrapper>
  );
};
